import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { type GatsbyLinkProps, Link as GatsbyLink } from 'gatsby';

import { ModalEnum } from '../ModalEnum';
import type RouteEnum from '../RouteEnum';
import { useRouteContext } from '../RouteProvider';
import type { EventType } from '../services/EventType';
import Tracking from '../services/Tracking';
import { openModal } from '../store/app';
import { isSignedInSelector } from '../store/selectors';

interface SharedProps {
  disabled?: boolean;
  track?: EventType | [EventType, ...UnsafeAny];
}

export interface InternalLinkProps<TState> extends SharedProps, Omit<GatsbyLinkProps<TState>, 'to' | 'onClick'> {
  to: RouteEnum | string;
  params?: Record<string, string>;
  search?: string;
  hash?: string;
  language?: string;
  privatePath?: boolean;

  onClick?(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): boolean | void | Promise<boolean | void>;
}

export interface ExternalLinkProps extends SharedProps, Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'onClick'> {
  href: string;

  onClick?(event: React.MouseEvent<HTMLAnchorElement, MouseEvent>): boolean | void | unknown;
}

export type LinkProps<TState> = InternalLinkProps<TState> | ExternalLinkProps;

export default function Link<TState>(props: LinkProps<TState>) {
  const dispatch = useDispatch();
  const isSignedIn = useSelector(isSignedInSelector);
  const ctx = useRouteContext();
  if ('to' in props) {
    const { to, params, search, hash, track, state, replace, privatePath, ...rest } =
      props as InternalLinkProps<TState>;
    const path = ctx.generatePath(to, params, search, hash);
    const prefixedPath = ctx.getPrefixedPath(path);

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      if (track) {
        const [type, ...args] = Array.isArray(track) ? track : [track];
        Tracking.track(type, ...args);
      }
      if (props.onClick) {
        const result = props.onClick(event);
        if (result === false || result instanceof Promise) {
          event.preventDefault();
          Promise.resolve(result).then(result => {
            if (result === false) return;
            ctx.navigate(path, { state, replace });
          });
          return false;
        }
      }
      if (privatePath && !isSignedIn) {
        event.preventDefault();
        dispatch(openModal(ModalEnum.SIGN_IN, { redirect: path }));
        return false;
      }
    };

    if (props.target) {
      return (
        <a href={prefixedPath} {...rest} onClick={handleClick}>
          {props.children}
        </a>
      );
    }

    return (
      // @ts-expect-error TS2769: No overload matches this call.  TS2786: GatsbyLink cannot be used as a JSX component. Its instance type GatsbyLink<unknown> is not a valid JSX element.
      <GatsbyLink to={prefixedPath} {...rest} onClick={handleClick}>
        {props.children}
      </GatsbyLink>
    );
  }

  const { href, track, ...rest } = props;
  const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    if (track) {
      const [type, ...args] = Array.isArray(track) ? track : [track];
      Tracking.track(type, ...args);
    }
    if (props.onClick) {
      const result = props.onClick(event);
      if (result === false) {
        event.preventDefault();
        return false;
      }
    }
  };

  return (
    <a {...rest} href={href} onClick={handleClick}>
      {props.children}
    </a>
  );
}
